import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";
import { useParams, useNavigate } from "react-router-dom";
import "./Branch.css";

const url = process.env.REACT_APP_BACKEND_URL;

const EditBranch = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState({
    name: "",
    district: "",
    department: "",
  });

  const [districts, setDistricts] = useState([]);
  const [departments, setDepartments] = useState([]);

  // Fetch all districts on mount
  useEffect(() => {
    const fetchDistricts = async () => {
      try {
        const res = await axios.get(`${url}/api/district`);
        setDistricts(res.data.data || []);
      } catch (error) {
        toast.error("Failed to load districts");
      }
    };
    fetchDistricts();
  }, []);

  // Fetch existing branch info
  useEffect(() => {
    const fetchBranch = async () => {
      try {
        const res = await axios.get(`${url}/api/branch/${id}`);
        const branch = res.data.data;
        setData({
          name: branch.name,
          district: branch.district?._id || "",
          department: branch.department?._id || "",
        });
      } catch (error) {
        toast.error("Failed to load branch details");
        navigate("/ViewBranch");
      } finally {
        setLoading(false);
      }
    };
    fetchBranch();
  }, [id, navigate]);

  // Fetch departments when district changes
  useEffect(() => {
    const fetchDepartments = async () => {
      if (!data.district) {
        setDepartments([]);
        return;
      }
      try {
        const res = await axios.get(`${url}/api/department?district=${data.district}`);
        setDepartments(res.data.data || []);
      } catch (error) {
        toast.error("Failed to load departments for this district");
      }
    };
    fetchDepartments();
  }, [data.district]);

  const onChangeHandler = (event) => {
    const { name, value } = event.target;
    setData((prevData) => ({
      ...prevData,
      [name]: value,
      ...(name === "district" ? { department: "" } : {}),
    }));
  };

  const onSubmitHandler = async (e) => {
    e.preventDefault();

    if (!data.name || !data.district || !data.department) {
      toast.error("All fields are required");
      return;
    }

    try {
      setSubmitting(true);
      const res = await axios.put(`${url}/api/branch/${id}`, data);
      if (res.data.success) {
        toast.success("Branch updated successfully!");
        navigate("/ViewBranch");
      }
    } catch (error) {
      toast.error(error.response?.data?.message || "Failed to update Branch");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading branch...</div>;

  return (
    <div className="district-container">
      <div className="header">
        <h2>Edit Branch</h2>
        <button onClick={() => navigate("/ViewBranch")} className="add-button">
          Back to List
        </button>
      </div>

      <form className="district-form" onSubmit={onSubmitHandler}>
        <div className="form-group">
          <label>Branch Name</label>
          <input
            type="text"
            name="name"
            value={data.name}
            onChange={onChangeHandler}
            required
          />
        </div>

        <div className="form-group">
          <label>District</label>
          <select
            name="district"
            value={data.district}
            onChange={onChangeHandler}
            required
          >
            <option value="">Select District</option>
            {districts.map((dist) => (
              <option key={dist._id} value={dist._id}>
                {dist.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Department</label>
          <select
            name="department"
            value={data.department}
            onChange={onChangeHandler}
            required
            disabled={!data.district}
          >
            <option value="">Select Department</option>
            {departments.map((dept) => (
              <option key={dept._id} value={dept._id}>
                {dept.name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-actions">
          <button type="submit" className="submit-button" disabled={submitting}>
            {submitting ? "Updating..." : "Update Branch"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/ViewBranch")}
            className="cancel-button"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default EditBranch;